// SEOCanonical — a headless component that keeps per-route SEO tags in sync with
// the current React Router path. Manages: <link rel="canonical">, <title>,
// <meta name="description">, and the corresponding Open Graph + Twitter card tags.
// No UI, no dependencies.
//
// Only PUBLIC pages get their own tags. Protected/utility pages fall back to the
// homepage canonical + tags so private URLs never accidentally canonicalize
// themselves for search engines.
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const CANONICAL_HOST = 'https://testgenieapp.com';

// Descriptions are kept aligned with the actual page content.
const PAGE_META = {
  '/': {
    title: 'TestGenie AI — AI-Powered Test Case Generator for QA Teams',
    description:
      'Turn software requirements into test scenarios, test cases and traceability with TestGenie AI. Generate QA documentation faster while keeping requirements, coverage and risks visible.',
  },
  '/pricing': {
    title: 'Pricing — TestGenie AI',
    description:
      'Simple, transparent pricing for TestGenie AI. Start free and upgrade to Pro when you need more capacity for requirements, scenarios and test cases.',
  },
  '/login': {
    title: 'Sign in — TestGenie AI',
    description:
      'Sign in to TestGenie AI to generate QA documentation, test scenarios and test cases from your requirements.',
  },
  '/signup': {
    title: 'Create an account — TestGenie AI',
    description:
      'Create your free TestGenie AI account and start turning requirements into test scenarios, test cases and traceability in minutes.',
  },
};

const setMeta = (selector, attr, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const [tag, key, name] = selector.match(/meta\[(name|property)="([^"]+)"\]/) || [];
    if (key && name) {
      el.setAttribute(key, name);
      document.head.appendChild(el);
    } else {
      return;
    }
    // Silence unused-var warnings
    void tag;
  }
  el.setAttribute(attr, value);
};

const setLink = (rel, href) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export const SEOCanonical = () => {
  const location = useLocation();

  useEffect(() => {
    const path = PAGE_META[location.pathname] ? location.pathname : '/';
    const meta = PAGE_META[path];
    const href = `${CANONICAL_HOST}${path}`;

    // Canonical
    setLink('canonical', href);

    // Title
    if (document.title !== meta.title) document.title = meta.title;

    // Meta description
    setMeta('meta[name="description"]', 'content', meta.description);

    // Open Graph
    setMeta('meta[property="og:url"]', 'content', href);
    setMeta('meta[property="og:title"]', 'content', meta.title);
    setMeta('meta[property="og:description"]', 'content', meta.description);

    // Twitter card
    setMeta('meta[name="twitter:title"]', 'content', meta.title);
    setMeta('meta[name="twitter:description"]', 'content', meta.description);
  }, [location.pathname]);

  return null;
};
